import React, { FC, useMemo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@/components/Select/index";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { useAppTheme } from "@/utils/theme";
import { CreateProgramFormData } from "./validation.schema";
import {
  CalculationMethodEnum,
  ValidationRateEnum,
  WeekdaysEnum,
  AssignCMCoordinatorEnum,
  FieldVisibilityEnum,
} from "@/domain/programs.enum";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Tooltip,
} from "@mui/material";
import { FormSection } from "@/ui/sections/FormSection";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEducationalResources } from "@/hooks/useEducationalResources";
import { EducationalResourceStatusEnum } from "@/domain/educational-resource.enum";

type TabProgramConfigurationProps = {
  editMode?: boolean;
};

export const TabProgramConfiguration: FC<TabProgramConfigurationProps> = ({
  editMode = false,
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation("program");
  const { t: tCommon } = useTranslation("common");

  const {
    watch,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<CreateProgramFormData>();

  const calculationMethodWatched = watch(
    "calculationMethod",
  ) as CalculationMethodEnum;
  const weekdaysWatched = watch("weekdays") as string[];
  const validationRatesWatched = watch("validationRates") as ValidationRateEnum;
  const enabledFormFactor2Watched = watch("enabledFormFactor2") as boolean;

  const {
    fields: eventFields,
    append: appendEvent,
    remove: removeEvent,
  } = useFieldArray({
    control,
    name: "events" as never,
  });

  const incentiveCalculationMethods = useMemo(() => {
    return Object.values(CalculationMethodEnum).map((it) => ({
      key: it,
      value: t(`enums.${it}` as any),
    }));
  }, []);

  const validationRateOptions = useMemo(() => {
    return Object.values(ValidationRateEnum).map((it) => ({
      key: it,
      value: t(`enums.${it}` as any),
    }));
  }, []);

  const weekdaysOptions = useMemo(() => {
    return Object.entries(WeekdaysEnum).map(([key, value]) => ({
      key: value,
      value: t(`enums.${key}` as any),
    }));
  }, []);

  const assignCMCoordinatorOptions = useMemo(() => {
    return Object.values(AssignCMCoordinatorEnum).map((it) => ({
      key: it,
      value: t(`enums.${it.toUpperCase()}` as any, it),
    }));
  }, []);

  const fieldVisibilityOptions = useMemo(() => {
    return Object.values(FieldVisibilityEnum).map((it) => ({
      key: it,
      value: t(`enums.${it.toUpperCase()}` as any, it),
    }));
  }, []);

  const formFactorTypeOptions = useMemo(() => {
    return [
      {
        key: "NYXOID_NASAL_SPRAY",
        value: t("form-factor-types.nyxoid-nasal-spray", "Nyxoid Nasal Spray"),
      },
      {
        key: "PEBBLE_NASAL_SPRAY",
        value: t("form-factor-types.pebble-nasal-spray", "Pebble Nasal Spray"),
      },
      {
        key: "PRENOXAD_INJECTABLE",
        value: t(
          "form-factor-types.prenoxad-injectable",
          "Prenoxad Injectable",
        ),
      },
    ];
  }, []);

  // Language options for participant user guide
  const languageOptions = [
    { key: "en-us", value: "English (US)" },
    { key: "en-gb", value: "English (UK)" },
    { key: "es-419", value: "Spanish (Latin America)" },
  ];

  // Field array for participantUserGuideURL
  const {
    fields: userGuideFields,
    append: appendUserGuide,
    remove: removeUserGuide,
  } = useFieldArray({
    control,
    name: "participantUserGuideURL" as never,
  });

  // Fetch educational resources for the select
  const { educationalResources, isLoadingEducationalResources } =
    useEducationalResources({
      sortField: "priority",
      sortOrder: 1,
      pageNumber: 0,
      pageSize: 100,
      search: "",
      status: [EducationalResourceStatusEnum.ACTIVE],
      type: [],
      categoryId: undefined,
    });

  // Educational materials options
  const educationalMaterialsOptions = useMemo(() => {
    if (!educationalResources) return [];
    return educationalResources.map((resource) => {
      const englishContent = resource.languages?.find(
        (lang) => lang.language === "en-us",
      );
      const title =
        englishContent?.title || resource.languages?.[0]?.title || resource._id;
      return {
        key: resource._id,
        value: title,
      };
    });
  }, [educationalResources]);

  return (
    <Grid
      container
      spacing={2}
      direction={"column"}
      sx={{ justifyContent: "flex-start" }}
    >
      <Grid size={{ xs: 12, md: 4 }} sx={{ maxHeight: "700px" }}>
        <FormSection title={t("sections.incentive-ladder")}>
          <Controller
            name="calculationMethod"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={incentiveCalculationMethods || []}
                value={(field.value as string) || ""}
                label={t("labels.incentive-calculation-method")}
                id="incentive-calculation-method"
                fullWidth
                error={!!errors.calculationMethod}
                errorMessage={errors.calculationMethod?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="basedIncentiveNegativeResults"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="based-incentive-for-negative-results"
                label={t("labels.based-incentive-for-negative-results")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.basedIncentiveNegativeResults}
                helperText={
                  errors.basedIncentiveNegativeResults
                    ? errors.basedIncentiveNegativeResults.message
                    : null
                }
                fullWidth
                type="number"
                required={true}
              />
            )}
          />
          <Controller
            name="basedIncentivePositiveResults"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="based-incentive-for-positive-results"
                label={t("labels.based-incentive-for-positive-results")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.basedIncentivePositiveResults}
                helperText={
                  errors.basedIncentivePositiveResults
                    ? errors.basedIncentivePositiveResults.message
                    : null
                }
                fullWidth
                type="number"
                required={true}
              />
            )}
          />
          <Controller
            name="incentiveAccountCreation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="incentive-account-creation"
                label="Incentive account creation"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.incentiveAccountCreation}
                helperText={
                  errors.incentiveAccountCreation
                    ? errors.incentiveAccountCreation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="incentiveFirstValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="incentive-first-validation"
                label="Incentive first validation"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.incentiveFirstValidation}
                helperText={
                  errors.incentiveFirstValidation
                    ? errors.incentiveFirstValidation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="incentiveValidationRequest"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="incentive-validation-request"
                label="Incentive validation request"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.incentiveValidationRequest}
                helperText={
                  errors.incentiveValidationRequest
                    ? errors.incentiveValidationRequest.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="incentiveOnDemandValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="incentive-on-demand-validation"
                label="Incentive on-demand validation"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.incentiveOnDemandValidation}
                helperText={
                  errors.incentiveOnDemandValidation
                    ? errors.incentiveOnDemandValidation.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="incentiveNonCarriage"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="incentive-non-carriage"
                label="Incentive non-carriage"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.incentiveNonCarriage}
                helperText={
                  errors.incentiveNonCarriage
                    ? errors.incentiveNonCarriage.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          {[
            CalculationMethodEnum["INCREMENTAL-LADDER"],
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"],
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"],
          ].indexOf(calculationMethodWatched) > -1 ? (
            <>
              <Controller
                name="incrementValueNegative"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="increment-value-for-negative-result"
                    label={t("labels.increment-value-for-negative-result")}
                    variant="outlined"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    error={!!errors.incrementValueNegative}
                    helperText={
                      errors.incrementValueNegative
                        ? errors.incrementValueNegative.message
                        : null
                    }
                    fullWidth
                    type="number"
                    required={true}
                  />
                )}
              />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pt: 1, pb: 1 }}
              >
                <Controller
                  name="maxIncentiveNegativeRe"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="max-incentive-for-negative-result"
                      label={t("labels.max-incentive-for-negative-result")}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.maxIncentiveNegativeRe}
                      helperText={
                        errors.maxIncentiveNegativeRe
                          ? errors.maxIncentiveNegativeRe.message
                          : null
                      }
                      fullWidth
                      type="number"
                      required={true}
                    />
                  )}
                />
                <Tooltip
                  title={t("tooltips.maxIncentiveNegativeResults")}
                  arrow
                >
                  <IconButton>
                    <InfoIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.dark }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : null}
          {[
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"],
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"],
          ].indexOf(calculationMethodWatched) > -1 ? (
            <Controller
              name="resetAmount"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="reset-amount"
                  label={t("labels.reset-amount")}
                  variant="outlined"
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={!!errors.resetAmount}
                  helperText={
                    errors.resetAmount ? errors.resetAmount.message : null
                  }
                  fullWidth
                  type="number"
                  required={true}
                />
              )}
            />
          ) : null}
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection
          title={t("sections.validation-limits")}
          tooltip={t("tooltips.validation-limits")}
        >
          <Controller
            name="timeToSubmitValidation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="time-to-submit-validation"
                label={t("labels.time-to-submit-validation")}
                variant="outlined"
                value={field.value}
                onChange={(value) => {
                  const inputValue = value.target.value;
                  if (/^\d*$/.test(inputValue)) {
                    field.onChange(value);
                  }
                }}
                error={!!errors.timeToSubmitValidation}
                helperText={
                  errors.timeToSubmitValidation
                    ? errors.timeToSubmitValidation.message
                    : null
                }
                fullWidth
                type="number"
                required={true}
              />
            )}
          />
          <Controller
            name="validationLimitReminder"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="validation-limit-reminder"
                label={t("labels.validation-limit-reminder")}
                variant="outlined"
                value={field.value}
                onChange={(value) => {
                  const inputValue = value.target.value;
                  if (/^\d*$/.test(inputValue)) {
                    field.onChange(value);
                  }
                }}
                error={!!errors.validationLimitReminder}
                helperText={
                  errors.validationLimitReminder
                    ? errors.validationLimitReminder.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.validation-rates")}>
          <Controller
            name="validationRates"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={validationRateOptions}
                value={(field.value as string) || ""}
                label={t("labels.validation-rates")}
                id="validation-rates"
                fullWidth
                error={!!errors.validationRates}
                errorMessage={errors.validationRates?.message}
                required={true}
              />
            )}
          />
          {validationRatesWatched === ValidationRateEnum.WEEKDAYS ? (
            <>
              <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                {weekdaysOptions.map((it) => {
                  const labelId = `checkbox-list-label-${it.key}`;
                  return (
                    <ListItem key={it.key} disablePadding>
                      <ListItemButton role={undefined} dense>
                        <ListItemIcon
                          sx={{
                            minWidth: "30px",
                          }}
                        >
                          <Checkbox
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ "aria-labelledby": labelId }}
                            checked={weekdaysWatched.some((x) => x == it.key)}
                            onChange={(e) => {
                              let newValue;
                              if (e.target.checked) {
                                newValue = [...weekdaysWatched, it.key];
                              } else {
                                newValue = (weekdaysWatched || []).filter(
                                  (x) => x !== it.key,
                                );
                              }
                              setValue("weekdays", newValue);
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={it.value} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
              {errors.weekdays && (
                <Typography color="error" variant="body2">
                  {errors.weekdays.message ||
                    tCommon("validations.validator_min_items", { count: 1 })}
                </Typography>
              )}
            </>
          ) : null}
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.contact")}>
          <Controller
            name="fullName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="full-name"
                label={t("labels.full-name")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.fullName}
                helperText={errors.fullName ? errors.fullName.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="email"
                label={t("labels.email")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="phone-number"
                label={t("labels.phone-number")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.phoneNumber}
                helperText={
                  errors.phoneNumber ? errors.phoneNumber.message : null
                }
                fullWidth
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection
          title={t("sections.re-enrollment")}
          tooltip={t("tooltips.re-enrollment")}
        >
          <Controller
            name="numberOfDays"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="number-of-days"
                label={t("labels.number-of-days")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.numberOfDays}
                helperText={
                  errors.numberOfDays ? errors.numberOfDays.message : null
                }
                fullWidth
                type="number"
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.form-factors", "Form Factors")}>
          <Controller
            name="formFactors"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={formFactorTypeOptions || []}
                value={(field.value as string[]) || []}
                label={t("labels.form-factor", "Form Factor")}
                id="form-factor"
                fullWidth
                multiple={true}
                error={!!errors.formFactors}
                errorMessage={errors.formFactors?.message}
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection
          title={t("sections.cm-coordinator", "CM Coordinator Settings")}
        >
          <Controller
            name="assignCMCoordinator"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={assignCMCoordinatorOptions || []}
                value={(field.value as string) || ""}
                label={t(
                  "labels.assign-cm-coordinator",
                  "Assign CM Coordinator",
                )}
                id="assign-cm-coordinator"
                fullWidth
                error={!!errors.assignCMCoordinator}
                errorMessage={errors.assignCMCoordinator?.message}
              />
            )}
          />
          <Controller
            name="enabledFormFactor2"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-form-factor-2",
                  "Enable Form Factor 2",
                )}
              />
            )}
          />
          <Controller
            name="mobilePhoneNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={fieldVisibilityOptions || []}
                value={(field.value as string) || ""}
                label={t("labels.mobile-phone-number", "Mobile Phone Number")}
                id="mobile-phone-number"
                fullWidth
                error={!!errors.mobilePhoneNumber}
                errorMessage={errors.mobilePhoneNumber?.message}
              />
            )}
          />
          <Controller
            name="mobileDateOfBirth"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={fieldVisibilityOptions || []}
                value={(field.value as string) || ""}
                label={t("labels.mobile-date-of-birth", "Mobile Date of Birth")}
                id="mobile-date-of-birth"
                fullWidth
                error={!!errors.mobileDateOfBirth}
                errorMessage={errors.mobileDateOfBirth?.message}
              />
            )}
          />
          <Controller
            name="enabledRewardUponAccountCreation"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t(
                  "labels.enabled-reward-upon-account-creation",
                  "Enable Reward Upon Account Creation",
                )}
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <FormSection title={t("sections.events", "Events")}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {eventFields.map((field, index) => (
              <Box
                key={field.id}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    {t("labels.event", "Event")} #{index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => removeEvent(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "none" }}>
                    <Controller
                      name={`events.${index}.eventId` as any}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="hidden"
                          sx={{ display: "none" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name={`events.${index}.validationRate` as any}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("labels.validation-rate", "Validation Rate")}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name={`events.${index}.rewardConfiguration` as any}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t(
                            "labels.reward-configuration",
                            "Reward Configuration",
                          )}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name={`events.${index}.validationLimits` as any}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t(
                            "labels.validation-limits",
                            "Validation Limits",
                          )}
                          variant="outlined"
                          type="number"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name={`events.${index}.validationReminders` as any}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t(
                            "labels.validation-reminders",
                            "Validation Reminders",
                          )}
                          variant="outlined"
                          type="number"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name={`events.${index}.needsCertificate` as any}
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={t(
                            "labels.needs-certificate",
                            "Needs Certificate",
                          )}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                appendEvent({
                  eventId: "",
                  validationRate: "",
                  rewardConfiguration: "",
                  validationLimits: null,
                  validationReminders: null,
                  needsCertificate: false,
                })
              }
              variant="outlined"
              size="small"
            >
              {t("buttons.add-event", "Add Event")}
            </Button>
          </Box>
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <FormSection
          title={t(
            "sections.participant-user-guide-url",
            "Participant User Guide URLs",
          )}
          tooltip={t(
            "tooltips.participant-user-guide-url",
            "Add URLs for participant user guides by language.",
          )}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {userGuideFields.map((field, index) => (
              <Box
                key={field.id}
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <Controller
                  name={`participantUserGuideURL.${index}.language` as const}
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={field.onChange}
                      data={languageOptions}
                      value={(field.value as string) || ""}
                      label={t("labels.language", "Language")}
                      id={`participant-user-guide-language-${index}`}
                      error={
                        Array.isArray(errors.participantUserGuideURL) &&
                        !!(errors.participantUserGuideURL[index] as any)
                          ?.language
                      }
                      errorMessage={
                        Array.isArray(errors.participantUserGuideURL)
                          ? (errors.participantUserGuideURL[index] as any)
                              ?.language?.message
                          : undefined
                      }
                      required
                      sx={{ minWidth: 180 }}
                    />
                  )}
                />
                <Controller
                  name={
                    `participantUserGuideURL.${index}.userGuideURL` as const
                  }
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id={`participant-user-guide-url-${index}`}
                      label={t("labels.user-guide-url", "User Guide URL")}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={
                        Array.isArray(errors.participantUserGuideURL) &&
                        !!(errors.participantUserGuideURL[index] as any)
                          ?.userGuideURL
                      }
                      helperText={
                        Array.isArray(errors.participantUserGuideURL)
                          ? (errors.participantUserGuideURL[index] as any)
                              ?.userGuideURL?.message
                          : undefined
                      }
                      fullWidth
                      required
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeUserGuide(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                appendUserGuide({ language: "", userGuideURL: "" })
              }
              variant="outlined"
              size="small"
            >
              {t("buttons.add-user-guide-url", "Add User Guide URL")}
            </Button>
          </Box>
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <FormSection
          title="Educational Materials"
          tooltip="Select educational resources to associate with this program."
        >
          <Controller
            name="educationalMaterials"
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={educationalMaterialsOptions}
                value={(field.value as string[]) || []}
                label="Educational Materials"
                id="educational-materials"
                multiple
                error={!!errors.educationalMaterials}
                errorMessage={(errors.educationalMaterials as any)?.message}
                disabled={isLoadingEducationalResources}
              />
            )}
          />
          {!isLoadingEducationalResources &&
            educationalMaterialsOptions.length === 0 && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                No educational resources available
              </Typography>
            )}
        </FormSection>
      </Grid>
    </Grid>
  );
};
